import convertCategoryToTree from '../utils/convertCategoryToTree';
import isSdaFormatReport from '../utils/isSdaFormatReport';
import convertElementToTree from '../utils/convertElementsToTree';
import convertScoreToTree from '../utils/convertScoreToTree';
import getFieldSettings from '@app.raytd.com/common/utils/test-suites/getFieldSettings';
import { useCallback, useContext } from 'react';
import { ReportGroupModes } from '../types';
import convertSpacesToTree from '../utils/convertSpacesToTree';
import convertTestSpecificationToTree from '../utils/convertTestsToTree';
import { ReportTestItem } from '../utils/entities';
import useReportItemFilters from '../utils/useReportItemsFilter';
import { defaultInitialFilters } from '../utils/useReportItemsFilter';

import { ReportDataProviderContext } from '@/app/features/reports/contexts/data-context';
import { useReportContext } from '@/app/features/reports/contexts/report-context';
import type useLiveReportData from '@/app/features/reports/hooks/use-live-report-data';
import { SelectedFilters } from '@/app/features/reports/types/filters';
import { TestSuite } from '@app.raytd.com/common/types/test-suite';
import { FieldSettingsMap } from '@app.raytd.com/common/types/fieldSettings';

export type { ReportFilters } from '@/app/features/reports/types/report-data';
export type useReportDataResult = ReturnType<typeof useLiveReportData>;

export const useReportData = (
  reportId: string,
  includedTestsOnly = false,
  viewId: string
) => {
  const dataProvider = useContext(ReportDataProviderContext);
  const { viewFiltersMap, setViewFilters } = useReportContext();

  const {
    data,
    bookmarks,
    missingData,
    missingSegments,
    missingDataCount,
    enrichedTestItems,
    report,
    testSuites,
    isLoading: isDataLoading,
    error,
    totalDataCount,
    revalidateData,
  } = dataProvider.useReportData(reportId, includedTestsOnly);

  const currentViewFilters = viewFiltersMap[viewId] || defaultInitialFilters;

  const setCurrentViewFilters = useCallback(
    (filters: SelectedFilters | ((prev: SelectedFilters) => SelectedFilters)) => {
        setViewFilters(viewId, filters);
    },
    [setViewFilters, viewId]
  );

  const {
    facets,
    filteredData,
    filteredCounts,
    unfilteredCounts,
    filtersActive,
    updateFilters,
    removeFilter,
    filterInput,
    setFilterInput,
  } = useReportItemFilters(
      enrichedTestItems, 
      report, 
      currentViewFilters,
      setCurrentViewFilters,
      viewId
    );

  const updateGroupedData = useCallback(
    (groupMode: ReportGroupModes, data: ReportTestItem[]) => {

      // we want to add the feildSettings to the data
      const dataWithFieldSettings = data.map((item) => {
        //find the testSuite that matches the item.testSuiteId
        const testSuite = report?.testSuites?.find((testSuite) => testSuite.id === item.testSpecification.testSuiteId);
        return {
          ...item,
          fieldSettings: getFieldSettings(testSuite as unknown as TestSuite) || {} as FieldSettingsMap
        } as ReportTestItem;
      });
      
      switch (groupMode) {
        case 'element':
          return convertElementToTree(dataWithFieldSettings);
        case 'location':
          return convertSpacesToTree(dataWithFieldSettings);
        case 'test':
          return convertTestSpecificationToTree(dataWithFieldSettings);
        case 'rating':
          return convertScoreToTree(dataWithFieldSettings, report?.testSuites);
        case 'category':
          return convertCategoryToTree(dataWithFieldSettings, {
            nestSubcategories: isSdaFormatReport(report),
          });
        default:
          console.error('Unknown group mode', groupMode);
          return data;
      }
    },
    [report?.testSuites, report]
  );

  return {
    data,
    testSuites,
    bookmarks,
    missingSegments,
    missingDataCount,
    report,
    enrichedTestItems,
    missingData,
    filteredData,
    totalDataCount,
    isLoading: isDataLoading,
    error: error,
    revalidateData,
    filters: {
      selectedFilters: currentViewFilters,
      facets,
      filteredCounts,
      unfilteredCounts,
      filtersActive,
      setSelectedFilters: setCurrentViewFilters,
      updateFilters,
      removeFilter,
      filterInput,
      setFilterInput,
    },
    updateGroupedData,
  };
};

export default useReportData;
